import { Howl, Howler } from 'howler'

// ---------------------------------------------------------------------------
// iOS Safari, and why nothing in here is ever allowed to pause.
//
// Safari starts every AudioContext `suspended` and only lets it resume from
// inside a real user gesture. Howler additionally auto-suspends the shared
// context 30s after the last *unpaused* sound. Chain those together and pausing
// the music becomes a trap: the context suspends while muted, the next play()
// defers on a ctx.resume() that never resolves outside a gesture, and because
// music and SFX share one context, ALL audio dies for the rest of the session.
//
// So the music Howl is created once, with autoplay + loop, and is never paused,
// stopped or unloaded. "Off" is a gain change on a still-playing source, which
// keeps the context `running` at all times and makes unmuting instant.
//
// Invariants — do not reintroduce pause()/stop()/unload() here, and do not wire
// this manager to visibilitychange/pagehide/blur. iOS manages the audio session
// itself; pausing on background reintroduces exactly the failure above.
//
// Diagnostic: `Howler.ctx.state` must read "running" at all times after the
// first tap, including while muted. If it ever reads "suspended", something has
// paused the music.
// ---------------------------------------------------------------------------

// Belt and braces for the one window this design can't close: between page load
// and the music Howl existing (we can't build it until auth tells us which game
// we are), there is no unpaused sound, so Howler's 30s auto-suspend could still
// fire on a slow connection. Disabling it removes the mechanism entirely.
// NOTE: this is `autoSuspend`, NOT `autoUnlock` — autoUnlock must stay at its
// default `true`, it is what binds Howler's touchstart/touchend/click/keydown
// unlock handlers for us.
Howler.autoSuspend = false

// Gain ramp applied when music is toggled on/off, in place of the old
// pause/resume fade. Same feel as before (~500ms), but purely a volume change.
const FADE_STEP = 0.08
const FADE_INTERVAL_MS = 40

// SFX briefly duck the music so clicks and win/lose stings cut through.
const DUCK_LEVEL = 0.35
const DUCK_RELEASE_MS = 500

class MusicManager {
  constructor() {
    this.howl = null
    this.enabled = true
    this.baseVolume = 0.6

    // Effective volume is always baseVolume * gain * duckFactor, resolved
    // through applyVolume(). Keeping the two modifiers as independent factors
    // means a duck landing mid-fade (or while muted) can never fight the fade
    // or accidentally unmute the music.
    this.gain = 0
    this.duckFactor = 1

    this.fadeTimer = null
    this.duckTimer = null
  }

  /**
   * Create the single music Howl. Deliberately NOT gated behind a user gesture:
   * `autoplay: true` parks the play request in Howler's deferred queue when
   * autoplay is blocked, and Howler's own unlock handlers flush it on the first
   * tap. That is what makes a "tap to enable sound" gate unnecessary.
   *
   * @param {string} src - URL of the background music file
   */
  init(src) {
    // Idempotence guard. The calling effect re-runs as `gameCode` resolves, and
    // StrictMode double-invokes it in dev; two looping music Howls is its own
    // bug. `gameCode` comes from launch params and is fixed for the life of the
    // page, so a second, *different* src can't legitimately arrive — switching
    // games is a full page load, which gives us a fresh module anyway.
    if (this.howl) return

    this.gain = 0

    this.howl = new Howl({
      src: [src],
      autoplay: true,
      loop: true,
      volume: 0,
      // No `html5: true` — that opts out of Web Audio and would need a
      // per-element iOS unlock. It would also make volume inert on iPhone,
      // where HTMLMediaElement.volume is read-only.
    })

    this.rampTo(this.enabled ? 1 : 0)
  }

  setEnabled(value) {
    this.enabled = value
    this.rampTo(value ? 1 : 0)
  }

  setVolume(value) {
    this.baseVolume = value
    this.applyVolume()
  }

  /** Dip the music for a moment so a sound effect reads clearly over it. */
  duck() {
    if (!this.howl) return

    clearTimeout(this.duckTimer)

    this.duckFactor = DUCK_LEVEL
    this.applyVolume()

    this.duckTimer = setTimeout(() => {
      this.duckFactor = 1
      this.applyVolume()
    }, DUCK_RELEASE_MS)
  }

  applyVolume() {
    if (!this.howl) return

    this.howl.volume(this.baseVolume * this.gain * this.duckFactor)
  }

  /** Ramp the on/off gain factor towards `target` (0 or 1). */
  rampTo(target) {
    clearInterval(this.fadeTimer)

    if (this.gain === target) {
      this.applyVolume()
      return
    }

    this.fadeTimer = setInterval(() => {
      this.gain =
        this.gain < target
          ? Math.min(this.gain + FADE_STEP, target)
          : Math.max(this.gain - FADE_STEP, target)

      this.applyVolume()

      if (this.gain === target) clearInterval(this.fadeTimer)
    }, FADE_INTERVAL_MS)
  }
}

export const musicManager = new MusicManager()
